/* ============================================
   Globdrop — Timer (requestAnimationFrame)
   ============================================ */

export type TimerCallback = (remaining: number) => void;
export type TimerDoneCallback = () => void;

/**
 * High-precision countdown timer using requestAnimationFrame.
 * No drift, battery-friendly, pauses when tab is hidden.
 */
export class CountdownTimer {
  private duration: number;
  private remaining: number;
  private startTime: number = 0;
  private rafId: number = 0;
  private running: boolean = false;
  private onTick: TimerCallback;
  private onDone: TimerDoneCallback;

  constructor(
    durationSeconds: number,
    onTick: TimerCallback,
    onDone: TimerDoneCallback
  ) {
    this.duration = durationSeconds;
    this.remaining = durationSeconds;
    this.onTick = onTick;
    this.onDone = onDone;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.startTime = performance.now();
    this.remaining = this.duration;
    this.onTick(this.remaining);
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  reset(newDuration?: number): void {
    this.stop();
    if (newDuration !== undefined) {
      this.duration = newDuration;
    }
    this.remaining = this.duration;
  }

  getRemaining(): number {
    return Math.max(0, this.remaining);
  }

  isRunning(): boolean {
    return this.running;
  }

  private tick = (now: number): void => {
    if (!this.running) return;

    const elapsed = (now - this.startTime) / 1000;
    this.remaining = Math.max(0, this.duration - elapsed);

    this.onTick(this.remaining);

    if (this.remaining <= 0) {
      this.running = false;
      this.onDone();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };
}
